/**
 * Session Service
 *
 * Client-side service for interacting with session management Cloud Functions.
 * Handles session creation, heartbeats, status checks, and termination.
 */

import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from './firebase';
import {
  SESSION_ERROR_CODES,
  type CreateSessionRequest,
  type CreateSessionResponse,
  type HeartbeatRequest,
  type HeartbeatResponse,
  type GetSessionStatusRequest,
  type GetSessionStatusResponse,
  type EndSessionRequest,
  type EndOtherSessionsRequest,
  type EndOtherSessionsResponse,
  type GetActiveSessionsResponse,
  type SessionErrorCode,
} from '../constants/session';

// ============================================================================
// SESSION ID STORAGE
// ============================================================================

const SESSION_ID_KEY = 'perfin_session_id';

export function getStoredSessionId(): string | null {
  return localStorage.getItem(SESSION_ID_KEY);
}

export function setStoredSessionId(sessionId: string): void {
  localStorage.setItem(SESSION_ID_KEY, sessionId);
}

export function clearStoredSessionId(): void {
  localStorage.removeItem(SESSION_ID_KEY);
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

export class SessionError extends Error {
  code: SessionErrorCode;

  constructor(code: SessionErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = 'SessionError';
  }
}

export function isSessionExpiredError(error: unknown): boolean {
  if (error instanceof SessionError) {
    return error.code === SESSION_ERROR_CODES.SESSION_EXPIRED;
  }
  // Check for Firebase Functions error format
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;
    return code === SESSION_ERROR_CODES.SESSION_EXPIRED ||
           code === 'functions/unauthenticated' ||
           code === 'functions/permission-denied';
  }
  return false;
}

// ============================================================================
// CLOUD FUNCTION CALLS
// ============================================================================

/**
 * Create a new session on the server.
 * Called after successful login.
 */
export async function createSession(): Promise<CreateSessionResponse> {
  const functions = getFirebaseFunctions();
  const callable = httpsCallable<CreateSessionRequest, CreateSessionResponse>(
    functions,
    'createSession'
  );

  const result = await callable({
    userAgent: navigator.userAgent,
  });

  // Store session ID for future requests
  setStoredSessionId(result.data.sessionId);

  return {
    sessionId: result.data.sessionId,
    expiresAt: new Date(result.data.expiresAt),
  };
}

/**
 * Send a heartbeat to keep the session alive.
 * Called periodically while the user is active.
 */
export async function sendHeartbeat(sessionId: string): Promise<HeartbeatResponse> {
  const functions = getFirebaseFunctions();
  const callable = httpsCallable<HeartbeatRequest, HeartbeatResponse>(
    functions,
    'sessionHeartbeat'
  );

  const result = await callable({ sessionId });

  return {
    status: result.data.status,
    expiresAt: new Date(result.data.expiresAt),
    remainingMs: result.data.remainingMs,
  };
}

/**
 * Get the current session status without updating lastActivityAt.
 * Used to check if session is still valid.
 */
export async function getSessionStatus(sessionId: string): Promise<GetSessionStatusResponse> {
  const functions = getFirebaseFunctions();
  const callable = httpsCallable<GetSessionStatusRequest, GetSessionStatusResponse>(
    functions,
    'getSessionStatus'
  );

  const result = await callable({ sessionId });

  return {
    status: result.data.status,
    expiresAt: new Date(result.data.expiresAt),
    remainingMs: result.data.remainingMs,
  };
}

/**
 * End the current session.
 * Called during logout.
 */
export async function endSession(sessionId: string): Promise<void> {
  const functions = getFirebaseFunctions();
  const callable = httpsCallable<EndSessionRequest, void>(
    functions,
    'endSession'
  );

  await callable({ sessionId });
  clearStoredSessionId();
}

/**
 * End all other sessions for the current user.
 * Called from Security Settings > "Logout All Other Devices".
 */
export async function endOtherSessions(currentSessionId: string): Promise<EndOtherSessionsResponse> {
  const functions = getFirebaseFunctions();
  const callable = httpsCallable<EndOtherSessionsRequest, EndOtherSessionsResponse>(
    functions,
    'endOtherSessions'
  );

  const result = await callable({ currentSessionId });
  return {
    endedCount: result.data.endedCount,
  };
}

/**
 * Get all active sessions for the current user.
 * Used to display in Security Settings.
 */
export async function getActiveSessions(currentSessionId: string): Promise<GetActiveSessionsResponse> {
  const functions = getFirebaseFunctions();
  const callable = httpsCallable<{ currentSessionId: string }, GetActiveSessionsResponse>(
    functions,
    'getActiveSessions'
  );

  const result = await callable({ currentSessionId });

  return {
    sessions: result.data.sessions.map(session => ({
      ...session,
      createdAt: new Date(session.createdAt),
      lastActivityAt: new Date(session.lastActivityAt),
    })),
  };
}
